import React, { ChangeEventHandler, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Input from '../../components/atoms/Fields/Input';
import { FieldMode } from '../../components/atoms/Fields/enums';
import { IconCart, IconSearch } from '../../assets/svg/icons';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import {
  StyledBrandCount,
  StyledBrandLogo,
  StyledBrandName,
  StyledBrandTile,
  StyledBrandsClear,
  StyledBrandsHeader,
  StyledBrandsScroll,
  StyledBrandsSection,
  StyledBrandsTitle,
  StyledBuyButton,
  StyledCard,
  StyledCardBody,
  StyledCardCategory,
  StyledCardDescription,
  StyledCardFooter,
  StyledCardImage,
  StyledCardTitle,
  StyledCartBadge,
  StyledCartButton,
  StyledCategoryBar,
  StyledCategoryChip,
  StyledEmptyState,
  StyledGrid,
  StyledMinQty,
  StyledPrice,
  StyledPriceBlock,
  StyledSearchForm,
  StyledTopBar,
} from './styles';
import ProductDetailModal from './ProductDetailModal';
import CartDrawer from './CartDrawer';
import CheckoutModal from './CheckoutModal';
import { CartItem, Product } from './types';
import { BRANDS, CATEGORIES, PRODUCTS } from './data';

const MarketplacePage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeBrand, setActiveBrand] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const { setHeaderTitle } = useHeaderTitleContext();
  const intl = useIntl();

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'mainNavigation.marketplace',
        defaultMessage: 'Marketplace',
      })
    );
  }, [intl, setHeaderTitle]);

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearch(e.target.value);
  };

  const brandsWithCounts = BRANDS.map((b) => ({
    ...b,
    productCount: PRODUCTS.filter((p) => p.brand === b.id).length,
  }));

  const filtered = PRODUCTS.filter((p) => {
    const matchesCategory =
      activeCategory === 'All' || p.category === activeCategory;
    const matchesBrand = !activeBrand || p.brand === activeBrand;
    const brandName =
      BRANDS.find((b) => b.id === p.brand)?.name.toLowerCase() || '';
    const q = search.toLowerCase();
    const matchesSearch =
      !search ||
      p.name.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      brandName.includes(q);
    return matchesCategory && matchesBrand && matchesSearch;
  });

  const handleAddToCart = (product: Product, qty: number) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [...prev, { product, qty }];
    });
    setCartOpen(true);
  };

  const handleRemoveFromCart = (productId: number) => {
    setCart((prev) => prev.filter((i) => i.product.id !== productId));
  };

  const handleStartCheckout = () => {
    setCartOpen(false);
    setCheckoutOpen(true);
  };

  const handleOrderComplete = () => {
    setCart([]);
    setCheckoutOpen(false);
  };

  const totalCartUnits = cart.reduce((sum, i) => sum + i.qty, 0);

  return (
    <>
      <StyledTopBar>
        <StyledSearchForm onSubmit={(e) => e.preventDefault()}>
          <Input
            mode={FieldMode.Tertiary}
            placeholder={intl.formatMessage({
              id: 'marketplace.search',
              defaultMessage: 'Search products…',
            })}
            isRounded
            prefixComponent={<IconSearch />}
            value={search}
            onChange={handleSearch}
            type='input'
          />
        </StyledSearchForm>

        <StyledCartButton
          type='button'
          onClick={() => setCartOpen(true)}
          aria-label='Open cart'
        >
          <IconCart />
          {cart.length > 0 && <StyledCartBadge>{cart.length}</StyledCartBadge>}
        </StyledCartButton>
      </StyledTopBar>

      <StyledBrandsSection>
        <StyledBrandsHeader>
          <StyledBrandsTitle>Shop by brand</StyledBrandsTitle>
          {activeBrand && (
            <StyledBrandsClear
              type='button'
              onClick={() => setActiveBrand(null)}
            >
              Clear brand filter
            </StyledBrandsClear>
          )}
        </StyledBrandsHeader>
        <StyledBrandsScroll>
          {brandsWithCounts.map((brand) => {
            const isActive = activeBrand === brand.id;
            return (
              <StyledBrandTile
                key={brand.id}
                active={isActive}
                type='button'
                onClick={() =>
                  setActiveBrand((prev) => (prev === brand.id ? null : brand.id))
                }
                title={`${brand.name} · ${brand.productCount} products`}
              >
                <StyledBrandLogo active={isActive}>
                  {brand.initial}
                </StyledBrandLogo>
                <StyledBrandName active={isActive}>{brand.name}</StyledBrandName>
                <StyledBrandCount>
                  {brand.productCount}{' '}
                  {brand.productCount === 1 ? 'item' : 'items'}
                </StyledBrandCount>
              </StyledBrandTile>
            );
          })}
        </StyledBrandsScroll>
      </StyledBrandsSection>

      <StyledCategoryBar>
        {CATEGORIES.map((cat) => (
          <StyledCategoryChip
            key={cat}
            active={activeCategory === cat}
            onClick={() => setActiveCategory(cat)}
            type='button'
          >
            {cat}
          </StyledCategoryChip>
        ))}
      </StyledCategoryBar>

      {filtered.length === 0 ? (
        <StyledEmptyState>No products match your search.</StyledEmptyState>
      ) : (
        <StyledGrid>
          {filtered.map((product) => (
            <StyledCard key={product.id}>
              <StyledCardImage bg={product.bg}>{product.emoji}</StyledCardImage>
              <StyledCardBody>
                <StyledCardCategory>{product.category}</StyledCardCategory>
                <StyledCardTitle>{product.name}</StyledCardTitle>
                <StyledCardDescription>{product.description}</StyledCardDescription>
                <StyledCardFooter>
                  <StyledPriceBlock>
                    <StyledPrice>
                      {product.priceQI.toLocaleString()}
                      <small>QI</small>
                    </StyledPrice>
                    <StyledMinQty>Min. {product.minQty} units</StyledMinQty>
                  </StyledPriceBlock>
                  <StyledBuyButton
                    type='button'
                    onClick={() => setSelectedProduct(product)}
                  >
                    Buy in bulk
                  </StyledBuyButton>
                </StyledCardFooter>
              </StyledCardBody>
            </StyledCard>
          ))}
        </StyledGrid>
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={handleAddToCart}
        />
      )}

      {cartOpen && (
        <CartDrawer
          items={cart}
          onClose={() => setCartOpen(false)}
          onRemove={handleRemoveFromCart}
          onCheckout={handleStartCheckout}
        />
      )}

      {checkoutOpen && (
        <CheckoutModal
          items={cart}
          onClose={() => setCheckoutOpen(false)}
          onComplete={handleOrderComplete}
        />
      )}
    </>
  );
};

export default MarketplacePage;
